import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { DocumentLookupQuerySchema, DocumentLookupResponseSchema, DocumentNeighboursSchema, } from './document-navigation.js';
import { RateStringSchema } from './fx.js';
import { ImportStatsSchema } from './document-stats.js';
import { DocumentDateRangeQuery, pagedQuery, pagedResponse } from './pagination.js';
import { CurrencySchema, IsoDateSchema, MessageSchema, MoneyStringSchema, PctStringSchema, QtyStringSchema, UnitPrecisionSchema, } from './primitives.js';
const c = initContract();
/**
 * PUR-06 — IMPORT ORDERS.
 *
 * Note what this contract does NOT carry: there is no `manualLanded` field on
 * any shape below. Owner ruling R1 / BR-PUR-06-24 — a costing figure lives in
 * the charge rows and nowhere else, so a typed total arrives through
 * `setLandedTotal`, which distributes it INTO those rows and returns the same
 * shipment everyone else sees.
 *
 * Every figure is shown twice, in the order currency and in base, because the
 * design note is explicit that "currencies are never silently combined".
 */
/**
 * §7's seven LINEAR stages, in order — the rail the stage machine walks
 * (`advance` moves one step along it). `cancelled` is deliberately NOT in
 * this array: it is terminal and reached only through `cancel`, never by
 * stepping, so nothing that indexes the rail ever lands on it.
 */
export const IMPORT_STAGES = [
    'draft',
    'confirmed',
    'shipped',
    'in_transit',
    'customs_clearance',
    'received',
    'closed',
];
/**
 * Wave 5E — every value `import_orders.stage` can hold: the rail plus the
 * terminal `cancelled`. Lockstep with the DB CHECK (20261027310000) and the
 * Drizzle enum. Lists and filters read THIS; the stage machine reads the rail.
 */
export const IMPORT_STAGE_VALUES = [...IMPORT_STAGES, 'cancelled'];
export const ImportStageSchema = z.enum(IMPORT_STAGE_VALUES);
export const INCOTERMS = ['EXW', 'FOB', 'CFR', 'CIF', 'DAP', 'DDP'];
export const CHARGE_CATEGORIES = ['freight', 'duty', 'insurance', 'handling', 'other'];
/**
 * Wave 5E — how the charges are spread over the lines. `by_value` and
 * `by_quantity` are §5d's two; `by_weight` and `by_volume` (EC-19-16's
 * "adding a weight column first is the only honest route") need every line
 * to carry the basis, or the choice is refused (`IMPORT_REFUSALS.basisMissing`).
 */
export const ALLOC_METHODS = ['by_value', 'by_quantity', 'by_weight', 'by_volume'];
export const AllocMethodSchema = z.enum(ALLOC_METHODS);
/**
 * GEN-136, GEN-137, GEN-138, GEN-139, GEN-140, GEN-141, GEN-142 RECORDED
 * HERE, at the one place the product holds files:
 * PART 4 wants attachments on EVERY document and master record (20 MB cap,
 * typed allowlist, who-and-when shown, never removable from a confirmed
 * document, internal notes never printed). The build has exactly this
 * import-slot implementation and nothing product-wide — the known-unknowns
 * list's "Attachments" row, still true. A product-wide attachments module
 * is an owner/phase decision; until then these seven rules are unbuilt
 * outside this screen. Flagged in the rule-queue report.
 */
export const DOCUMENT_SLOTS = [
    'commercial_invoice',
    'packing_list',
    'bill_of_lading',
    'certificate_of_origin',
    'insurance_certificate',
];
export const IMPORT_MESSAGES = {
    /** ERR-PUR-06-01 — the dates are interpolated, as the spec shows them. */
    arrivesBeforeShipping: (arrive, ship) => `The goods cannot arrive on ${arrive} before they ship on ${ship}.`,
    /** ERR-PUR-06-02 */
    negativeCharge: 'A charge cannot be less than zero.',
    /** ERR-PUR-06-03 */
    chargeNeedsDescription: 'Type a description for this charge.',
    /** ERR-PUR-06-04 */
    receiveBeforeCustoms: 'Goods can only be received once the shipment reaches customs clearance.',
    /** ERR-PUR-06-05 — "You ordered 200 LED Panel 40W. You cannot receive 220." */
    overReceipt: (ordered, item, attempted) => `You ordered ${ordered} ${item}. You cannot receive ${attempted}.`,
    /**
     * BR-PUR-06-26's "never by silently retyping the running total", the half a
     * refusal can enforce: a typed total BELOW what a line has already received
     * used to return 200 and change nothing. Shaped after `overReceipt` — the
     * figure, the name, the figure. It names no correction route because the
     * build has none yet (the rule's other half, blocked on
     * `grn-landed-cost` — see the service).
     */
    lowerThanReceived: (received, item, attempted) => `You have received ${received} ${item}. You cannot lower that to ${attempted}.`,
    /** ERR-PUR-06-06 */
    outstandingUnits: (units) => `${units} units are still to arrive. Receive them, or record the shortfall.`,
    /** ERR-PUR-06-07 */
    closedShipment: 'This shipment is closed. Raise a landed-cost adjustment instead.',
    /** BR-PUR-06-20: no backward move without a typed reason. */
    reversalNeedsReason: 'Type why this shipment is going back a stage.',
    reversalAfterReceipt: 'Goods have been received. This shipment cannot go back a stage.',
    notFound: 'No such import order.',
    alreadyImported: 'This purchase order already has an import order.',
    /**
     * Wave 1C — the shipment is denominated in the ORDER's currency: a
     * different code would price the landed cost in money the supplier never
     * quoted. The shape is the bill's own currency rule (BR-PUR-03-07).
     */
    currencyMismatch: (orderCode, currency) => `Order ${orderCode} is in ${currency}. The shipment must be in ${currency}.`,
    /**
     * Wave 1C — an order already received (its status, or a posted goods
     * receipt behind it) has its stock counted once already; a shipment that
     * received it again would count it twice.
     */
    alreadyReceived: (orderCode) => `${orderCode} has already been received. An import order cannot be raised for it.`,
    /**
     * Wave 5C — the goods on this shipment's order came in through a posted
     * goods receipt, so they are on the shelf already; receiving them here as
     * well would count them twice. Their landed cost is posted onto that
     * receipt when the shipment reaches Received.
     */
    receivedThroughGrn: 'These goods were received through a goods receipt. Their landed cost is added to that receipt when the shipment reaches Received.',
    // ── wave 5E ────────────────────────────────────────────────────────────────
    /** GEN-59: the cancelling act carries its reason. */
    cancelReasonRequired: 'A shipment can only be cancelled with a reason, which is stored on it and written to the audit log.',
    /** A cancelled shipment is terminal and read-only. */
    cancelledShipment: 'This shipment is cancelled. Raise a new import order instead.',
    /**
     * Goods are on the shelf: cancelling the shipment would leave stock valued
     * at a landed cost that no longer has a shipment behind it. `ref` is the
     * IMP-YYYY-### reference.
     */
    cancelAfterReceipt: (ref) => `${ref} has goods received against it and cannot be cancelled. Return them through a supplier return instead.`,
    /** The order date names the year the reference was minted in (GEN-77a). */
    orderDateYearFixed: 'The order date must stay within the year the reference was issued in.',
    /** The order date is open while the shipment is a draft and fixed after. */
    orderDateLocked: 'The order date can only be changed while the shipment is a draft.',
    /**
     * Direct imports (no purchase order) are a company choice —
     * `purchasing.direct_import_enabled`, off by default. A zero-argument
     * function rather than a string so the D46 census counts it as a
     * converted member with a builder (`IMPORT_REFUSALS.directImportDisabled`).
     */
    directImportDisabled: () => 'Direct imports are switched off. Raise a purchase order first, or turn on direct imports in Settings.',
    /** A direct import names its supplier and its lines itself. */
    directImportNeedsSupplierAndLines: 'A shipment with no purchase order needs a supplier and at least one line.',
    /**
     * Apportioning by weight or volume needs the basis on EVERY line, or the
     * lines without one would silently take no share. `basis` is the raw token
     * (`weight` | `volume`).
     */
    basisMissing: (basis) => `Apportioning by ${basis} needs a ${basis} on every line.`,
    /**
     * Customs clearance files a declaration per line: the tariff code and the
     * country of origin have to be on the line before the shipment reaches
     * that stage. `itemCode` is the line's SKU.
     */
    hsCodeRequired: (itemCode) => `${itemCode} needs an HS code and a country of origin before customs clearance.`,
    /** A charge may only be tied to a POSTED bill of this company. */
    chargeBillNotPosted: 'A charge can only be linked to a posted bill.',
    /** The exchange rate on a charge is units per base, like the header rate. */
    chargeRateRequired: 'A charge in another currency needs its exchange rate.',
};
/**
 * D46 — PUR-06's four parameterised shipment refusals, structured. Every param
 * is a display value: the two ISO dates on `arrivesBeforeShipping`, the ordered
 * / received / attempted quantity strings (PRE-FORMATTED by the service, item
 * names embedded verbatim) and `outstandingUnits`' figure. No token, no plural
 * — the sentences carry a fixed English noun. Sentence parity pinned by
 * `refusal-census.spec.ts`.
 */
export const IMPORT_REFUSALS = {
    arrivesBeforeShipping: (arrive, ship) => ({
        message: IMPORT_MESSAGES.arrivesBeforeShipping(arrive, ship),
        key: 'imports.errArrivesBeforeShipping',
        params: { arrive, ship },
    }),
    overReceipt: (ordered, item, attempted) => ({
        message: IMPORT_MESSAGES.overReceipt(ordered, item, attempted),
        key: 'imports.errOverReceipt',
        params: { ordered, item, attempted },
    }),
    lowerThanReceived: (received, item, attempted) => ({
        message: IMPORT_MESSAGES.lowerThanReceived(received, item, attempted),
        key: 'imports.errLowerThanReceived',
        params: { received, item, attempted },
    }),
    outstandingUnits: (units) => ({
        message: IMPORT_MESSAGES.outstandingUnits(units),
        key: 'imports.errOutstandingUnits',
        params: { units },
    }),
    // ── wave 1C: the two order-naming refusals at the create door ─────────────
    /** `orderCode` is the order's formatted code; `currency` a three-letter code. */
    currencyMismatch: (orderCode, currency) => ({
        message: IMPORT_MESSAGES.currencyMismatch(orderCode, currency),
        key: 'imports.errCurrencyMismatch',
        params: { code: orderCode, currency },
    }),
    alreadyReceived: (orderCode) => ({
        message: IMPORT_MESSAGES.alreadyReceived(orderCode),
        key: 'imports.errAlreadyReceived',
        params: { code: orderCode },
    }),
    // ── wave 5E: cancel, direct imports and the landed-cost rules ─────────────
    /** `ref` is the shipment's IMP-YYYY-### reference, a display value. */
    cancelAfterReceipt: (ref) => ({
        message: IMPORT_MESSAGES.cancelAfterReceipt(ref),
        key: 'imports.errCancelAfterReceipt',
        params: { ref },
    }),
    /** No params: the setting's name is in the sentence, not a value. */
    directImportDisabled: () => ({
        message: IMPORT_MESSAGES.directImportDisabled(),
        key: 'imports.errDirectImportDisabled',
        params: {},
    }),
    /** `basis` is a TOKEN (`weight` | `volume`) — the dictionary selects on it. */
    basisMissing: (basis) => ({
        message: IMPORT_MESSAGES.basisMissing(basis),
        key: 'imports.errBasisMissing',
        params: { basis },
    }),
    /** `itemCode` is the line's SKU, a display value. */
    hsCodeRequired: (itemCode) => ({
        message: IMPORT_MESSAGES.hsCodeRequired(itemCode),
        key: 'imports.errHsCodeRequired',
        params: { itemCode },
    }),
};
/**
 * §5c's seven columns. `landedUnitCost` is the headline the screen exists to
 * produce, and it is RECOMPUTED on every read from the charge rows — §15:
 * "the allocation must be recalculated from the stored charge rows on every
 * read, never cached".
 */
export const ImportLineSchema = z
    .object({
    id: z.uuid(),
    lineNo: z.number().int(),
    itemId: z.uuid(),
    sku: z.string(),
    name: z.string(),
    qty: z.string(),
    /**
     * N-4 — the item's STOCK unit. An import line carries no unit of its own,
     * and the stock unit is what the goods are counted in when they land.
     */
    unitSymbol: z.string(),
    unitPrecision: UnitPrecisionSchema,
    /** In the ORDER currency, not in dollars. */
    unitPrice: MoneyStringSchema,
    hsCode: z.string().nullable(),
    originCountry: z.string().nullable(),
    /**
     * Wave 5E — the two apportioning bases §5b never captured (EC-19-16).
     * Null until typed; the by-weight / by-volume methods refuse while any
     * line is null.
     */
    weightKg: z.string().nullable(),
    volumeM3: z.string().nullable(),
    /**
     * Wave 5E — the duty rate on this line's tariff code, a percentage.
     * Entering customs clearance pre-fills the duty charge from
     * Σ line value × this, when the duty row is still empty.
     */
    customsDutyPct: z.string().nullable(),
    lineCost: MoneyStringSchema,
    /** This line's share of the charges, order currency. */
    chargeShare: MoneyStringSchema,
    landedUnitCost: MoneyStringSchema,
    /** The same figure in base — shown with a ≈, never silently combined. */
    landedUnitCostBase: MoneyStringSchema,
    receivedQty: z.string(),
})
    .strict();
export const ImportChargeSchema = z
    .object({
    id: z.uuid(),
    category: z.enum(CHARGE_CATEGORIES),
    label: z.string().nullable(),
    /** In the CHARGE's own currency (wave 5E) — the import's unless `currency` says otherwise. */
    amount: MoneyStringSchema,
    /**
     * Wave 5E — the currency this charge was billed in and the rate it was
     * FROZEN at when entered (units per base, like the header rate). A charge
     * entered before wave 5E carries the import's currency and follows the
     * header rate, which is what `null` on the rate means.
     */
    currency: z.string(),
    exchangeRate: z.string().nullable(),
    /** The charge in the IMPORT's currency — what the allocation spreads. */
    amountOrder: MoneyStringSchema,
    amountBase: MoneyStringSchema,
    isUserAdded: z.boolean(),
    /** Wave 5E — the posted bill (PUR-03) this charge is the invoice for, when linked. */
    purchaseInvoiceId: z.uuid().nullable(),
    purchaseInvoiceCode: z.string().nullable(),
})
    .strict();
export const ImportOrderSchema = z
    .object({
    id: z.uuid(),
    /** IMP-YYYY-### — the only number in the product carrying a year. */
    ref: z.string(),
    stage: ImportStageSchema,
    /**
     * Wave 5E — null on a DIRECT import (no purchase order behind it; the
     * lines were typed on the shipment). Fixed for life either way.
     */
    purchaseOrderId: z.uuid().nullable(),
    purchaseOrderCode: z.string().nullable(),
    supplierId: z.uuid(),
    supplierName: z.string(),
    /** Wave 5E — free text and the supplier's/forwarder's own reference. */
    notes: z.string().nullable(),
    manualRef: z.string().nullable(),
    orderDate: IsoDateSchema,
    shipDate: IsoDateSchema,
    arriveDate: IsoDateSchema,
    incoterm: z.enum(INCOTERMS),
    carrier: z.string().nullable(),
    trackingNo: z.string().nullable(),
    shipMethod: z.enum(['sea', 'air', 'land']),
    currencyCode: z.string(),
    /**
     * Units per ONE base unit — converting to base DIVIDES (GEN-109a).
     * A bare string on the READ side deliberately: the column is
     * numeric(18,6), so a stored 7.2500 echoes back as "7.250000" and the
     * 4-decimal `RateStringSchema` that bounds what may be TYPED (the
     * create/update bodies below) would refuse every stored row.
     */
    rate: z.string(),
    ratePolicy: z.enum(['order_date', 'payment_date']),
    allocMethod: AllocMethodSchema,
    warehouseId: z.uuid(),
    warehouseName: z.string(),
    lines: z.array(ImportLineSchema),
    charges: z.array(ImportChargeSchema),
    /** §5d's Summary card — exactly these three rows, in this order. */
    summary: z
        .object({
        itemsCost: MoneyStringSchema,
        itemsCostBase: MoneyStringSchema,
        chargesTotal: MoneyStringSchema,
        chargesTotalBase: MoneyStringSchema,
        totalLanded: MoneyStringSchema,
        totalLandedBase: MoneyStringSchema,
    })
        .strict(),
    receiving: z
        .object({
        orderedQty: z.string(),
        receivedQty: z.string(),
        percent: z.number().int(),
        /** The two badges never show together (§5f). */
        state: z.enum(['none', 'partial', 'all']),
        /** BR-PUR-06-10: the card appears from customs clearance onward. */
        open: z.boolean(),
        /**
         * Wave 5C — the goods came in through posted goods receipts on the
         * shipment's purchase order: this screen does not receive them again,
         * and the landed cost goes onto those receipts at Received.
         */
        throughGoodsReceipt: z.boolean(),
    })
        .strict(),
    documents: z.array(z
        .object({
        id: z.uuid(),
        slot: z.enum(DOCUMENT_SLOTS),
        fileName: z.string(),
        uploadedAt: z.string(),
    })
        .strict()),
    closedAt: z.string().nullable(),
    /** Wave 5E — GEN-59's stored reason, and when; null unless cancelled. */
    cancelReason: z.string().nullable(),
    cancelledAt: z.string().nullable(),
})
    .strict();
/**
 * Wave 5E — a line typed straight onto a DIRECT import (no purchase order).
 * The item names the stock unit the goods land in; the price is in the
 * shipment's currency, as a PO line's would have been.
 */
export const ImportDirectLineInputSchema = z
    .object({
    itemId: z.uuid(),
    qty: QtyStringSchema,
    unitPrice: MoneyStringSchema,
})
    .strict();
/** Wave 5E — one charge row as typed (`setCharges`). */
export const ImportChargeInputSchema = z
    .object({
    category: z.enum(CHARGE_CATEGORIES),
    label: z.string().max(120).optional(),
    amount: MoneyStringSchema,
    /**
     * The charge's own currency; absent = the import's. A different code
     * needs `exchangeRate` (units per base, PUR-06 §5 field 15's 4 decimals)
     * unless the FIN-05 table can supply one for the order date — the service
     * freezes whichever on the row.
     */
    currency: CurrencySchema.optional(),
    exchangeRate: RateStringSchema.optional(),
    /** The posted bill this charge is the invoice for. */
    purchaseInvoiceId: z.uuid().optional(),
})
    .strict();
/**
 * One row of PUR-06 §4's list. Named rather than inline so `pagedResponse` can
 * wrap it — the rows keep the `orders` key they already had, so paging is
 * additive on the wire (PAGINATION.md §3 step 1).
 */
export const ImportOrderRowSchema = z
    .object({
    id: z.uuid(),
    ref: z.string(),
    stage: ImportStageSchema,
    supplierName: z.string(),
    arriveDate: IsoDateSchema,
    totalLandedBase: MoneyStringSchema,
})
    .strict();
export const importsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/imports',
        query: pagedQuery({
            stage: ImportStageSchema.optional(),
            /** GEN-50 / the header rail's Find: the IMP-YYYY-### reference or the supplier's name. */
            q: z.string().trim().max(120).optional(),
            /** Wave 3B — bounds on the shipment's order date, both inclusive. */
            ...DocumentDateRangeQuery,
        }),
        responses: {
            200: pagedResponse('orders', ImportOrderRowSchema),
        },
        summary: 'PUR-06 — the shipments (purchasing.view), PAGED (NFR-42). The LIMIT bounds the per-row landed-cost recomputation, not merely the wire.',
    },
    create: {
        method: 'POST',
        path: '/api/imports',
        body: z
            .object({
            /**
             * BR-PUR-06-01: from a purchase order, and never changed after.
             * Wave 5E — OR absent, for a DIRECT import: then `supplierId` and
             * `lines` carry what the order would have (gated by the company
             * setting `purchasing.direct_import_enabled`).
             */
            purchaseOrderId: z.uuid().optional(),
            supplierId: z.uuid().optional(),
            lines: z.array(ImportDirectLineInputSchema).min(1).max(500).optional(),
            /** Wave 5E — free text and the supplier's/forwarder's own reference. */
            notes: z.string().max(2000).optional(),
            manualRef: z.string().trim().max(60).optional(),
            warehouseId: z.uuid(),
            orderDate: IsoDateSchema,
            shipDate: IsoDateSchema,
            arriveDate: IsoDateSchema,
            incoterm: z.enum(INCOTERMS),
            shipMethod: z.enum(['sea', 'air', 'land']),
            currencyCode: CurrencySchema,
            /**
             * PUR-06 §5 field 15: "Decimal, 4 decimals" — the same bound every
             * other exchange rate rides (FIN-05 §15 via `RateStringSchema`), and
             * GEN-49's refusal to build an unbounded decimal. Positive too: every
             * base figure on the shipment DIVIDES by this rate (GEN-109a), so a
             * zero rate is a division by zero wearing a valid shape.
             */
            rate: RateStringSchema,
            ratePolicy: z.enum(['order_date', 'payment_date']).optional(),
            carrier: z.string().max(120).optional(),
            trackingNo: z.string().max(60).optional(),
        })
            .strict(),
        responses: { 201: ImportOrderSchema, 400: MessageSchema, 409: MessageSchema },
        summary: 'PUR-06 — created from a purchase order, which is why the link is read-only',
    },
    get: {
        method: 'GET',
        path: '/api/imports/:importId',
        pathParams: z.object({ importId: z.uuid() }),
        responses: { 200: ImportOrderSchema, 404: MessageSchema },
        summary: 'PUR-06 — the allocation is recomputed here on every read (§15)',
    },
    /**
     * S4 — the document header's ‹‹ ›› rail (wave 2 of specs/erp-gap-analysis.md):
     * the import orders on either side of this one in number order (null at an
     * end). See `document-navigation.ts`.
     */
    neighbours: {
        method: 'GET',
        path: '/api/imports/:importId/neighbours',
        pathParams: z.object({ importId: z.uuid() }).strict(),
        responses: {
            200: DocumentNeighboursSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'The previous and next import order by number (purchasing.view).',
    },
    /** S4 — the "Go to no." box: one import order by its printed number; 404 on a miss. */
    lookup: {
        method: 'GET',
        path: '/api/imports/lookup',
        query: DocumentLookupQuerySchema,
        responses: {
            200: DocumentLookupResponseSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One import order by its printed number (purchasing.view).',
    },
    update: {
        method: 'PATCH',
        path: '/api/imports/:importId',
        pathParams: z.object({ importId: z.uuid() }),
        body: z
            .object({
            /** Wave 5E — editable while the shipment is a DRAFT, within the reference's year (GEN-77a). */
            orderDate: IsoDateSchema.optional(),
            shipDate: IsoDateSchema.optional(),
            arriveDate: IsoDateSchema.optional(),
            carrier: z.string().max(120).optional(),
            trackingNo: z.string().max(60).optional(),
            /** Wave 5E — null clears. */
            notes: z.string().max(2000).nullable().optional(),
            manualRef: z.string().trim().max(60).nullable().optional(),
            incoterm: z.enum(INCOTERMS).optional(),
            shipMethod: z.enum(['sea', 'air', 'land']).optional(),
            /** Same bound as `create` — PUR-06 §5 field 15's 4 decimals, > 0. */
            rate: RateStringSchema.optional(),
            ratePolicy: z.enum(['order_date', 'payment_date']).optional(),
            /** §5d: the choice changes what stock is worth, so it is stored. */
            allocMethod: AllocMethodSchema.optional(),
        })
            .strict(),
        responses: { 200: ImportOrderSchema, 400: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'PUR-06 §5b/§5d — header and costing choices',
    },
    /**
     * Wave 5E — the per-line customs and apportioning facts: the tariff code
     * and origin the declaration needs (required before customs clearance),
     * the duty rate that pre-fills the duty charge, and the weight / volume
     * the two new apportioning methods spread by. Null clears a field.
     */
    updateLine: {
        method: 'PATCH',
        path: '/api/imports/:importId/lines/:lineId',
        pathParams: z.object({ importId: z.uuid(), lineId: z.uuid() }).strict(),
        body: z
            .object({
            hsCode: z.string().trim().max(20).nullable().optional(),
            originCountry: z.string().trim().max(60).nullable().optional(),
            weightKg: QtyStringSchema.nullable().optional(),
            volumeM3: QtyStringSchema.nullable().optional(),
            customsDutyPct: PctStringSchema.nullable().optional(),
        })
            .strict(),
        responses: { 200: ImportOrderSchema, 400: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'PUR-06 §5c — HS code, origin, duty rate, weight and volume on one line (purchasing.edit)',
    },
    setCharges: {
        method: 'PUT',
        path: '/api/imports/:importId/charges',
        pathParams: z.object({ importId: z.uuid() }),
        body: z
            .object({
            charges: z.array(ImportChargeInputSchema),
        })
            .strict(),
        responses: { 200: ImportOrderSchema, 400: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'PUR-06 §5d — the ONE store of a costing figure (BR-PUR-06-17); wave 5E: each row in its own currency at a frozen rate, optionally naming its posted bill',
    },
    /**
     * Wave 5E — cancel the shipment. Allowed from any stage before goods are
     * received; terminal; the one-per-PO rule ignores a cancelled shipment so
     * the order can be shipped again. GEN-59: reason required, stored on the
     * row and written to the `import_order.cancel` audit row.
     */
    cancel: {
        method: 'POST',
        path: '/api/imports/:importId/cancel',
        pathParams: z.object({ importId: z.uuid() }).strict(),
        body: z
            .object({
            reason: z.string().trim().min(1, IMPORT_MESSAGES.cancelReasonRequired).max(500),
        })
            .strict(),
        responses: {
            200: ImportOrderSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'PUR-06 — cancel a shipment before any goods are received (purchasing.approve). Refused after receipt with IMPORT_REFUSALS.cancelAfterReceipt.',
    },
    setLandedTotal: {
        method: 'PUT',
        path: '/api/imports/:importId/landed-total',
        pathParams: z.object({ importId: z.uuid() }),
        body: z.object({ total: MoneyStringSchema }).strict(),
        responses: { 200: ImportOrderSchema, 400: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'PUR-06 §5e — an INPUT CONVENIENCE: distributes the total into the charge rows and allocates it like any other charge. Never a second store (BR-PUR-06-24)',
    },
    advance: {
        method: 'POST',
        path: '/api/imports/:importId/stage',
        pathParams: z.object({ importId: z.uuid() }),
        body: z
            .object({
            /** The RAIL only — `cancelled` is reached through `cancel`, never stepped to (wave 5E). */
            stage: z.enum(IMPORT_STAGES),
            /** BR-PUR-06-20: a backward move needs a typed reason. */
            reason: z.string().max(500).optional(),
        })
            .strict(),
        responses: { 200: ImportOrderSchema, 400: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'PUR-06 §7 — forward one stage, or back with a reason (the gap the prototype left)',
    },
    receive: {
        method: 'POST',
        path: '/api/imports/:importId/receive',
        pathParams: z.object({ importId: z.uuid() }),
        body: z
            .object({
            /** R7 is a running cumulative total, so this is the NEW total per line. */
            lines: z
                .array(z
                .object({
                lineId: z.uuid(),
                receivedQty: z.string(),
                /**
                 * BR-MD-03-17, the same two fields PUR-02's goods receipt
                 * carries: the units arriving in THIS delivery — the delta,
                 * never the running total, because a serial is named once and
                 * the second container brings different ones.
                 */
                serialNos: z.string().trim().min(1).max(60).array().max(1000).optional(),
                batchNo: z.string().trim().min(1).max(40).optional(),
            })
                .strict())
                .min(1),
            /** BR-PUR-06-19: a receipt date, which the prototype never captured. */
            receiptDate: IsoDateSchema,
        })
            .strict(),
        responses: { 200: ImportOrderSchema, 400: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'PUR-06 §5f — from customs clearance onward; each edit writes a dated GRN for the DIFFERENCE and values stock at the landed cost (BR-PUR-06-12/-13)',
    },
    /** Wave 3B — the list's tiles over the WHOLE register (see `document-stats.ts`). */
    stats: {
        method: 'GET',
        path: '/api/imports/stats',
        responses: {
            200: ImportStatsSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'PUR-06 list tiles (purchasing.view): shipments in transit, arriving within 14 days, landed value in transit, landed with no cost allocated — base currency, in SQL.',
    },
});
//# sourceMappingURL=imports.js.map